import { useEffect, useId, useState, type ReactNode } from 'react';

/**
 * CollapsibleCard — 見出しを押すとその場で中身が開閉するカード (2026-10-19)。
 *
 * 管理画面の地区概要で「基本情報」「参加者用の URL と QR コード」などのパネルを、別ページへ
 * 移動せず同じページ内で展開できるようにするための部品。
 *
 * 設計:
 *   - 見出しは `<button aria-expanded aria-controls>`。キーボード (Enter / Space) で開閉できる。
 *   - 閉じている間も中身は mount したまま `hidden` で隠す (入力途中の値を失わない)。
 *   - `summary` は閉じていても見出しの横に出る短い状態表示 (例:「非公開中」バッジ)。
 *   - `storageKey` を渡すと開閉状態をその端末のブラウザに覚える。保存できない環境
 *     (プライベートモード等) でも動作は変わらない。
 */
export interface CollapsibleCardProps {
  title: ReactNode;
  /** 閉じていても見出しの下に出る短い説明・状態表示。 */
  summary?: ReactNode;
  children: ReactNode;
  /** 初期状態で開くか (storageKey に保存値があればそちらを優先)。既定 false。 */
  defaultOpen?: boolean;
  /** 開閉状態を localStorage に覚えるときのキー。 */
  storageKey?: string;
  className?: string;
  testId?: string;
}

function readStored(key: string | undefined): boolean | null {
  if (!key) return null;
  try {
    const v = window.localStorage.getItem(key);
    return v === '1' ? true : v === '0' ? false : null;
  } catch {
    return null;
  }
}

function writeStored(key: string | undefined, open: boolean): void {
  if (!key) return;
  try {
    window.localStorage.setItem(key, open ? '1' : '0');
  } catch {
    // 保存できない環境では覚えないだけ
  }
}

export function CollapsibleCard({
  title,
  summary,
  children,
  defaultOpen = false,
  storageKey,
  className = '',
  testId,
}: CollapsibleCardProps) {
  const [open, setOpen] = useState<boolean>(() => readStored(storageKey) ?? defaultOpen);
  const bodyId = useId();

  useEffect(() => {
    writeStored(storageKey, open);
  }, [storageKey, open]);

  return (
    <div
      className={`bg-white rounded-lg shadow border border-slate-200 min-w-0 ${className}`.trim()}
      data-testid={testId}
      data-open={open ? 'true' : 'false'}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start gap-3 text-left px-4 py-3 sm:px-6 sm:py-4 rounded-lg hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
      >
        <span className="flex-1 min-w-0">
          <span className="block text-base font-bold text-slate-900 break-words">{title}</span>
          {summary ? (
            <span className="block mt-0.5 text-xs text-slate-500 break-words">{summary}</span>
          ) : null}
        </span>
        <span className="shrink-0 inline-flex items-center gap-1 text-sm text-primary-600 pt-0.5">
          {open ? '閉じる' : '開く'}
          <svg
            width="14"
            height="14"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
            className={`transition-transform ${open ? 'rotate-180' : ''}`}
          >
            <path d="M5.3 7.3a1 1 0 0 1 1.4 0L10 10.6l3.3-3.3a1 1 0 1 1 1.4 1.4l-4 4a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 0-1.4z" />
          </svg>
        </span>
      </button>
      <div
        id={bodyId}
        hidden={!open}
        className="px-4 pb-4 sm:px-6 sm:pb-6 pt-3 border-t border-slate-100 min-w-0"
      >
        {children}
      </div>
    </div>
  );
}
